import React, { useState } from "react";

import { DateTime } from "luxon";

import { Typography, styled } from "@mui/material";

import ConfirmationModal, {
  ConfirmationModalProps,
} from "@rui/ConfirmationModal";
import DatePicker from "@rui/DatePicker";
import Stack from "@rui/Stack";
import TextField from "@rui/TextField";
import TimePicker from "@rui/TimePicker";

import type { Upload, UploadMetadata } from "./types";

const StyledTextField = styled(TextField)(({ theme }) => ({
  ".MuiInputBase-input": {
    fontSize: "14px",
    height: "32px",
  },
  ".MuiInputBase-formControl": {
    fontWeight: 400,
    height: "32px",
  },
  ".MuiOutlinedInput-notchedOutline": {
    height: "37px",
    borderColor: theme.palette.shades.neutral[100],
    borderRadius: "4px",
    color: theme.palette.shades.neutral[800],
  },
}));

const EditMetadataModal = ({
  upload,
  onConfirm,
  ...props
}: Omit<ConfirmationModalProps, "onConfirm"> & {
  upload: Upload;
  onConfirm: (uploadId: Upload["id"], options: UploadMetadata) => void;
}) => {
  if (!upload) return null;

  const [title, setTitle] = useState(upload.name);
  const [startTime, setStartTime] = useState(
    upload.startTime ?? DateTime.now()
  );
  const [date, setDate] = useState(upload.startTime ?? DateTime.now());

  const onSaveEdits = () => {
    // merge date from date picker with time from time picker
    const mergedStartTime = date?.set({
      hour: startTime?.hour,
      minute: startTime?.minute,
    });

    onConfirm(upload.localId, {
      title,
      startTime: mergedStartTime,
    });
    props.onClose();
  };

  return (
    <ConfirmationModal
      title="Edit metadata"
      modalProps={{
        hideBackdrop: true,
        sx: {
          ".MuiPaper-root": {
            boxShadow: "0px 0px 10px 5px rgba(0, 0, 0, 0.04) ",
          },
        },
      }}
      {...props}
      onConfirm={onSaveEdits}
    >
      <Stack spacing={0.5}>
        <Typography variant="labelReg">Meeting Name</Typography>
        <StyledTextField
          value={title}
          onChange={(e) => {
            setTitle(e.target.value);
          }}
        />
      </Stack>
      <Stack spacing={0.5}>
        <Typography variant="labelReg">Date</Typography>
        <DatePicker
          defaultValue={date}
          onChange={(newValue) => setDate(newValue ?? DateTime.now())}
          disableFuture
        />
      </Stack>
      <Stack spacing={0.5}>
        <Typography variant="labelReg">Time</Typography>
        <TimePicker
          placeholder="Start time"
          defaultValue={startTime}
          onChange={(newValue) => setStartTime(newValue ?? DateTime.now())}
        />
      </Stack>
    </ConfirmationModal>
  );
};

export default EditMetadataModal;
